"use client";
import {
  CheckBox,
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
} from "@/components";
import { IServiceFormProps } from "@/interfaces";
import { useTranslations } from "next-intl";
import TextField from "../../ui/textField";
import {
  convertRialToToman,
  convertTomanToRial,
} from "persian-money-converter";
import React, { useEffect, useState } from "react";
import ToggleCard from "../../toggleCard";
import { cn } from "@/lib";
import { BdCopy } from "solar-icon-react/bd";
import { useToastStore } from "@/store/toastStore";

const moneyUnitTypes: {
  label: string;
  key: number;
  isSelected: boolean;
}[] = [
  { label: "تومان به ریال", key: 1, isSelected: true },
  { label: "ریال به تومان", key: 2, isSelected: false },
];

const toEnglishDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

function separateByComma(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const MoneyConvertorInquiryForm = ({}: IServiceFormProps) => {
  const [amount, setAmount] = useState<string>("");
  const [numericResult, setNumericResult] = useState<string>("");
  const [charResult, setCharResult] = useState<string>("");
  const [toggleUnitType, setToggleUnitType] = useState(moneyUnitTypes);
  const { setOpenToast } = useToastStore();
  const selectedKey = toggleUnitType.find((item) => item.isSelected)?.key;
  const t = useTranslations();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = toEnglishDigits(e.target.value).replace(/,/g, "");

    if (!/^\d*$/.test(raw)) return;

    setAmount(raw);
  };
  useEffect(() => {
    setNumericResult("");
    setCharResult("");
  }, [selectedKey]);
  useEffect(() => {
    if (!amount || !selectedKey) {
      setNumericResult("");
      setCharResult("");
      return;
    }

    const numericAmount = Number(amount);
    if (selectedKey === 1) {
      const result = convertTomanToRial(numericAmount);
      setNumericResult(result.toNumber);
      setCharResult(`${result.toChar} ریال `);
    } else if (selectedKey === 2) {
      const result = convertRialToToman(numericAmount);
      setNumericResult(result.toNumber);
      setCharResult(`${result.toChar} تومان `);
    }
  }, [amount, selectedKey]);

  const copyToClipboard = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setOpenToast({
          variant: "success",
          message: "کپی شد",
        });
      })
      .catch((err) => {
        console.error("خطا در کپی:", err);
      });
  };

  return (
    <FlexBetweenCol gap={8} className="w-full">
      <FlexBetween gap={3} className="xl:gap-6">
        {toggleUnitType.map(({ label, isSelected, key }, idx) => (
          <ToggleCard
            key={idx}
            className={cn(
              "hover:text-secondary-main h-10 grow border-gray-300 bg-white py-0 transition-colors xl:h-12 xl:px-4",
              isSelected && "border-primary-main shadow-main",
            )}
            isSelected={isSelected}
            nodeEnd={<CheckBox isChecked={isSelected} />}
            setSelected={() =>
              setToggleUnitType((prev) =>
                prev.map((item) =>
                  item.key === key
                    ? { ...item, isSelected: true }
                    : { ...item, isSelected: false },
                ),
              )
            }
          >
            <p>{label}</p>
          </ToggleCard>
        ))}
      </FlexBetween>

      <TextField
        hasShadow
        placeholder={t("serviceFormSubmissions.moneyConvertor.amount")}
        aria-label="مبلغ"
        autoFocus
        inputMode="numeric"
        value={amount ? separateByComma(Number(amount)) : ""}
        onChange={handleAmountChange}
        inputClassName="dir-ltr h-10"
      />

      {numericResult !== "" && (
        <div className="bg-primary-main flex w-full items-center justify-between rounded-md border p-3 text-white">
          <div>{numericResult}</div>
          <div>
            <CustomButton
              id="money-convertor-copy-numeric"
              className={cn("px-2! text-white shadow-none")}
              onClick={() => copyToClipboard(numericResult.replace(/,/g, ""))}
            >
              <BdCopy size={18} />
              کپی
            </CustomButton>
          </div>
        </div>
      )}
      {charResult !== "" && (
        <div className="bg-primary-main flex w-full items-center justify-between rounded-md border p-3 text-white">
          <div>{charResult}</div>
          <div>
            <CustomButton
              id="money-convertor-copy-char"
              className={cn("px-2! text-white shadow-none")}
              onClick={() => copyToClipboard(charResult)}
            >
              <BdCopy size={18} />
              کپی
            </CustomButton>
          </div>
        </div>
      )}
    </FlexBetweenCol>
  );
};

export default MoneyConvertorInquiryForm;
